/**
 * Guest favourite shops (browser-only) — Adobe/Shopify-style localStorage set.
 * Key `customer:pendingFavoriteSpContacts`, max 20, TTL 90d.
 * Copied into the account on every establish for **new** keys
 * (`transferGuestFavoritesOnRegistrationSilent`); browser list is retained.
 */

import {
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
  type LocalizedNameMap,
  type LogoChipBackgroundSettings,
  type LogoChipRimSettings,
} from 'pi-kiosk-shared';
import { isSalesPointCustomerDiscoveryListable } from '../shop/customerDiscoveryListability.js';
import {
  parseSalesPointCustomerPresence,
  type SalesPointCustomerPresence,
} from '../shops/customerPresence.js';
import type { PendingSpContactRef } from './salesPointContactsTypes.js';

export const PENDING_FAVORITE_SP_CONTACTS_STORAGE_KEY = 'customer:pendingFavoriteSpContacts';
/** Mirrors logged-in `favoriteOptOut` — visit must not re-auto-favourite after unstar. */
export const PENDING_FAVORITE_SP_CONTACT_OPT_OUT_STORAGE_KEY =
  'customer:pendingFavoriteSpContactOptOuts';
export const PENDING_FAVORITE_SP_CONTACTS_CHANGED_EVENT =
  'customer:pendingFavoriteSpContacts-changed';
export const PENDING_FAVORITE_SP_CONTACTS_MAX = 20;
export const PENDING_FAVORITE_SP_CONTACTS_TTL_MS = 90 * 24 * 60 * 60 * 1000;

function notifyPendingFavoriteSpContactsChanged(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(PENDING_FAVORITE_SP_CONTACTS_CHANGED_EVENT));
}

/** Subscribe to same-tab + cross-tab favourite list changes (for nav href recompute). */
export function subscribePendingFavoriteSpContacts(onChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  window.addEventListener(PENDING_FAVORITE_SP_CONTACTS_CHANGED_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(PENDING_FAVORITE_SP_CONTACTS_CHANGED_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/** Snapshot for `useSyncExternalStore` — raw localStorage string. */
export function getPendingFavoriteSpContactsSnapshot(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return window.localStorage.getItem(PENDING_FAVORITE_SP_CONTACTS_STORAGE_KEY) ?? '';
}

export function getPendingFavoriteSpContactsServerSnapshot(): string {
  return '';
}

/** Display snapshot so Oblíbené works offline without a public directory round-trip. */
export interface PendingFavoriteSpContact extends PendingSpContactRef, LogoChipRimSettings, LogoChipBackgroundSettings {
  code: string;
  name: string;
  /** Per-locale display-name overrides; null/omit = use `name`. */
  nameLocales?: LocalizedNameMap | null;
  location: string | null;
  /** A13b — Shipping-only display; omit → STOREFRONT on read. */
  customerPresence?: SalesPointCustomerPresence;
  tenantId: number;
  tenantName: string;
  customerShopUrl: string;
  imageUrl: string | null;
  imageUrlDark: string | null;
  tenantLogoUrl: string | null;
  tenantLogoUrlDark: string | null;
}

/**
 * Stage/auto-stage accept partial rim — upsert always fills defaults via
 * `normalizeLogoChipRimSettings`. Callers (G2 auto-stage paths) must forward
 * the four rim fields from live SP/contact DTO or `normalizeLogoChipRimSettings`
 * so non-default brand-kit rim survives localStorage round-trip.
 */
export type StagePendingFavoriteSpContactInput = Omit<
  PendingFavoriteSpContact,
  | 'seenAt'
  | keyof LogoChipRimSettings
  | keyof LogoChipBackgroundSettings
  | 'imageUrlDark'
  | 'tenantLogoUrlDark'
> &
  Partial<Pick<PendingFavoriteSpContact, 'imageUrlDark' | 'tenantLogoUrlDark'>> &
  Partial<LogoChipRimSettings> &
  Partial<LogoChipBackgroundSettings> & {
    seenAt?: string;
    nowMs?: number;
  };

interface StoredPendingFavoriteSpContact extends PendingFavoriteSpContact {
  storedAt: number;
}

function dedupeKey(contact: PendingSpContactRef): string {
  if (typeof contact.salesPointId === 'number' && contact.salesPointId > 0) {
    return `id:${contact.salesPointId}`;
  }
  const slug = (contact.salesPointSlug ?? '').trim().toLowerCase();
  return `slug:${contact.tenantCode.trim().toLowerCase()}|${slug}`;
}

function isExpired(storedAt: number, nowMs: number): boolean {
  return nowMs - storedAt > PENDING_FAVORITE_SP_CONTACTS_TTL_MS;
}

function readRaw(): StoredPendingFavoriteSpContact[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(PENDING_FAVORITE_SP_CONTACTS_STORAGE_KEY);
    if (raw === null || raw.length === 0) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item): item is StoredPendingFavoriteSpContact => {
      if (item === null || typeof item !== 'object') {
        return false;
      }
      const row = item as StoredPendingFavoriteSpContact;
      return (
        typeof row.tenantCode === 'string' &&
        row.tenantCode.length > 0 &&
        typeof row.seenAt === 'string' &&
        typeof row.storedAt === 'number' &&
        typeof row.name === 'string' &&
        typeof row.code === 'string' &&
        typeof row.tenantName === 'string' &&
        typeof row.customerShopUrl === 'string' &&
        typeof row.tenantId === 'number' &&
        (typeof row.salesPointId === 'number' ||
          (typeof row.salesPointSlug === 'string' && row.salesPointSlug.length > 0))
      );
    });
  } catch {
    return [];
  }
}

function writeRaw(items: StoredPendingFavoriteSpContact[]): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      PENDING_FAVORITE_SP_CONTACTS_STORAGE_KEY,
      JSON.stringify(items),
    );
    notifyPendingFavoriteSpContactsChanged();
  } catch {
    /* quota / private mode */
  }
}

function pruneAndDedupe(
  items: StoredPendingFavoriteSpContact[],
  nowMs: number,
): StoredPendingFavoriteSpContact[] {
  const fresh = items.filter((item) => !isExpired(item.storedAt, nowMs));
  const byKey = new Map<string, StoredPendingFavoriteSpContact>();
  for (const item of fresh) {
    byKey.set(dedupeKey(item), item);
  }
  const deduped = Array.from(byKey.values()).sort((a, b) => b.storedAt - a.storedAt);
  return deduped.slice(0, PENDING_FAVORITE_SP_CONTACTS_MAX);
}

function toPublic(item: StoredPendingFavoriteSpContact): PendingFavoriteSpContact {
  const rim = normalizeLogoChipRimSettings(item);
  const background = normalizeLogoChipBackgroundSettings(item);
  return {
    tenantCode: item.tenantCode,
    seenAt: item.seenAt,
    code: item.code,
    name: item.name,
    ...(item.nameLocales !== undefined ? { nameLocales: item.nameLocales } : {}),
    location: item.location,
    customerPresence: parseSalesPointCustomerPresence(item.customerPresence),
    tenantId: item.tenantId,
    tenantName: item.tenantName,
    customerShopUrl: item.customerShopUrl,
    imageUrl: item.imageUrl,
    imageUrlDark: item.imageUrlDark ?? null,
    tenantLogoUrl: item.tenantLogoUrl,
    tenantLogoUrlDark: item.tenantLogoUrlDark ?? null,
    ...rim,
    ...background,
    ...(typeof item.salesPointId === 'number' ? { salesPointId: item.salesPointId } : {}),
    ...(typeof item.salesPointSlug === 'string' && item.salesPointSlug.length > 0
      ? { salesPointSlug: item.salesPointSlug }
      : {}),
  };
}

/** Read guest favourites (TTL-pruned, deduped, capped). */
export function readPendingFavoriteSpContacts(
  nowMs: number = Date.now(),
): PendingFavoriteSpContact[] {
  const existing = readRaw();
  const pruned = pruneAndDedupe(existing, nowMs);
  if (pruned.length !== existing.length) {
    writeRaw(pruned);
  }
  return pruned.map(toPublic);
}

/** Claim body refs only (no display snapshot). */
export function readPendingFavoriteSpContactRefs(
  nowMs: number = Date.now(),
): PendingSpContactRef[] {
  return readPendingFavoriteSpContacts(nowMs).map(
    ({ tenantCode, salesPointId, salesPointSlug, seenAt }) => ({
      tenantCode,
      seenAt,
      ...(typeof salesPointId === 'number' ? { salesPointId } : {}),
      ...(typeof salesPointSlug === 'string' && salesPointSlug.length > 0
        ? { salesPointSlug }
        : {}),
    }),
  );
}

export function isPendingFavoriteSalesPointId(
  salesPointId: number,
  nowMs: number = Date.now(),
): boolean {
  return readPendingFavoriteSpContacts(nowMs).some(
    (item) => item.salesPointId === salesPointId,
  );
}

interface GuestFavoriteOptOutRef {
  tenantCode?: string;
  salesPointId?: number;
  salesPointSlug?: string;
  storedAt: number;
}

function optOutDedupeKey(ref: {
  tenantCode?: string;
  salesPointId?: number;
  salesPointSlug?: string;
}): string | null {
  if (typeof ref.salesPointId === 'number' && ref.salesPointId > 0) {
    return `id:${ref.salesPointId}`;
  }
  const tenant = (ref.tenantCode ?? '').trim().toLowerCase();
  const slug = (ref.salesPointSlug ?? '').trim().toLowerCase();
  if (tenant.length > 0 && slug.length > 0) {
    return `slug:${tenant}|${slug}`;
  }
  return null;
}

function readOptOutRaw(): GuestFavoriteOptOutRef[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(PENDING_FAVORITE_SP_CONTACT_OPT_OUT_STORAGE_KEY);
    if (raw === null || raw.length === 0) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((item): item is GuestFavoriteOptOutRef => {
      if (item === null || typeof item !== 'object') {
        return false;
      }
      const row = item as GuestFavoriteOptOutRef;
      return typeof row.storedAt === 'number' && optOutDedupeKey(row) !== null;
    });
  } catch {
    return [];
  }
}

function writeOptOutRaw(items: GuestFavoriteOptOutRef[]): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      PENDING_FAVORITE_SP_CONTACT_OPT_OUT_STORAGE_KEY,
      JSON.stringify(items),
    );
  } catch {
    /* quota / private mode */
  }
}

function pruneOptOuts(items: GuestFavoriteOptOutRef[], nowMs: number): GuestFavoriteOptOutRef[] {
  const fresh = items.filter((item) => !isExpired(item.storedAt, nowMs));
  const byKey = new Map<string, GuestFavoriteOptOutRef>();
  for (const item of fresh) {
    const key = optOutDedupeKey(item);
    if (key !== null) {
      byKey.set(key, item);
    }
  }
  return Array.from(byKey.values())
    .sort((a, b) => b.storedAt - a.storedAt)
    .slice(0, PENDING_FAVORITE_SP_CONTACTS_MAX);
}

export function isGuestFavoriteOptedOut(
  ref: { tenantCode?: string; salesPointId?: number; salesPointSlug?: string },
  nowMs: number = Date.now(),
): boolean {
  const key = optOutDedupeKey(ref);
  if (key === null) {
    return false;
  }
  return pruneOptOuts(readOptOutRaw(), nowMs).some((item) => optOutDedupeKey(item) === key);
}

export function markGuestFavoriteOptOut(
  ref: { tenantCode?: string; salesPointId?: number; salesPointSlug?: string },
  nowMs: number = Date.now(),
): void {
  const key = optOutDedupeKey(ref);
  if (key === null) {
    return;
  }
  writeOptOutRaw(
    pruneOptOuts(
      [
        {
          storedAt: nowMs,
          ...(typeof ref.salesPointId === 'number' && ref.salesPointId > 0
            ? { salesPointId: ref.salesPointId }
            : {}),
          ...(typeof ref.tenantCode === 'string' && ref.tenantCode.length > 0
            ? { tenantCode: ref.tenantCode }
            : {}),
          ...(typeof ref.salesPointSlug === 'string' && ref.salesPointSlug.trim().length > 0
            ? { salesPointSlug: ref.salesPointSlug.trim() }
            : {}),
        },
        ...readOptOutRaw(),
      ],
      nowMs,
    ),
  );
}

export function clearGuestFavoriteOptOut(
  ref: { tenantCode?: string; salesPointId?: number; salesPointSlug?: string },
  nowMs: number = Date.now(),
): void {
  const key = optOutDedupeKey(ref);
  if (key === null) {
    return;
  }
  writeOptOutRaw(
    pruneOptOuts(readOptOutRaw(), nowMs).filter((item) => optOutDedupeKey(item) !== key),
  );
}

function upsertPendingFavorite(input: StagePendingFavoriteSpContactInput): void {
  const nowMs = input.nowMs ?? Date.now();
  const seenAt = input.seenAt ?? new Date(nowMs).toISOString();
  const rim = normalizeLogoChipRimSettings(input);
  const background = normalizeLogoChipBackgroundSettings(input);
  const next: StoredPendingFavoriteSpContact = {
    tenantCode: input.tenantCode,
    seenAt,
    storedAt: nowMs,
    code: input.code,
    name: input.name,
    ...(input.nameLocales !== undefined ? { nameLocales: input.nameLocales } : {}),
    location: input.location,
    customerPresence: parseSalesPointCustomerPresence(input.customerPresence),
    tenantId: input.tenantId,
    tenantName: input.tenantName,
    customerShopUrl: input.customerShopUrl,
    imageUrl: input.imageUrl,
    imageUrlDark: input.imageUrlDark ?? null,
    tenantLogoUrl: input.tenantLogoUrl,
    tenantLogoUrlDark: input.tenantLogoUrlDark ?? null,
    ...rim,
    ...background,
    ...(typeof input.salesPointId === 'number' && input.salesPointId > 0
      ? { salesPointId: input.salesPointId }
      : {}),
    ...(typeof input.salesPointSlug === 'string' && input.salesPointSlug.trim().length > 0
      ? { salesPointSlug: input.salesPointSlug.trim() }
      : {}),
  };
  if (
    next.salesPointId === undefined &&
    (next.salesPointSlug === undefined || next.salesPointSlug.length === 0)
  ) {
    return;
  }
  writeRaw(pruneAndDedupe([next, ...readRaw()], nowMs));
}

/**
 * Explicit guest star (directory CTA) — clears opt-out so the shop can be favourited again.
 */
export function stagePendingFavoriteSpContact(
  input: StagePendingFavoriteSpContactInput,
): void {
  clearGuestFavoriteOptOut(input, input.nowMs ?? Date.now());
  upsertPendingFavorite(input);
}

/**
 * Visit auto-favourite (logged-in record analogue). Skips shops the guest unstarred.
 * G2 — fail-closed: only stage when Obchody SP allowlist proves listable (`=== true`).
 * Unknown / non-payReady (Cafe/Tester) never enter LS as favourites.
 */
export function autoStagePendingFavoriteOnVisit(
  input: StagePendingFavoriteSpContactInput,
): void {
  if (isGuestFavoriteOptedOut(input, input.nowMs ?? Date.now())) {
    return;
  }
  if (
    isSalesPointCustomerDiscoveryListable({
      salesPointId: input.salesPointId,
      tenantCode: input.tenantCode,
      code: input.code,
      salesPointSlug: input.salesPointSlug,
    }) !== true
  ) {
    return;
  }
  upsertPendingFavorite(input);
}

export function removePendingFavoriteSpContact(
  salesPointId: number,
  nowMs: number = Date.now(),
): void {
  const current = pruneAndDedupe(readRaw(), nowMs);
  const removed = current.find((item) => item.salesPointId === salesPointId);
  if (removed !== undefined) {
    markGuestFavoriteOptOut(removed, nowMs);
  } else {
    markGuestFavoriteOptOut({ salesPointId }, nowMs);
  }
  writeRaw(current.filter((item) => item.salesPointId !== salesPointId));
}

/**
 * G7 — mirror signed-in favorite toggle into guest LS.
 * Unfavorite → drop staged + mark opt-out so logout+visit cannot re-auto-stage.
 * Favorite → clear opt-out so explicit restar / visit can favour again.
 */
export function syncGuestFavoriteOptOutFromAccountToggle(
  ref: { tenantCode?: string; salesPointId?: number; salesPointSlug?: string },
  favorite: boolean,
  nowMs: number = Date.now(),
): void {
  if (favorite) {
    clearGuestFavoriteOptOut(ref, nowMs);
    return;
  }
  if (typeof ref.salesPointId === 'number' && ref.salesPointId > 0) {
    removePendingFavoriteSpContact(ref.salesPointId, nowMs);
    return;
  }
  markGuestFavoriteOptOut(ref, nowMs);
}

/** After claim — drop activated refs from the guest favourite store. */
export function removeActivatedPendingFavoriteSpContacts(
  activated: PendingSpContactRef[],
  nowMs: number = Date.now(),
): void {
  if (activated.length === 0) {
    writeRaw(pruneAndDedupe(readRaw(), nowMs));
    return;
  }
  const activatedKeys = new Set(activated.map(dedupeKey));
  const remaining = pruneAndDedupe(readRaw(), nowMs).filter(
    (item) => !activatedKeys.has(dedupeKey(item)),
  );
  writeRaw(remaining);
}

export function clearPendingFavoriteSpContacts(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(PENDING_FAVORITE_SP_CONTACTS_STORAGE_KEY);
    notifyPendingFavoriteSpContactsChanged();
  } catch {
    /* ignore */
  }
}

/**
 * Live public SP / tenant row used to refresh guest Oblíbené logo + rim + background + store photo.
 * SP sources prefer `imageUrl` / `imageUrlDark` + `tenantLogoUrl` / `tenantLogoUrlDark` + id/slug
 * match; tenant sources use `logoUrl` / `logoUrlDark` + `tenantCode` when no SP row is available.
 */
export type PendingFavoriteLiveBrandingSource = Partial<LogoChipRimSettings> &
  Partial<LogoChipBackgroundSettings> & {
  readonly salesPointId?: number;
  readonly tenantCode?: string;
  readonly code?: string;
  readonly salesPointSlug?: string;
  /** Public sales-point store photo (square thumb). */
  readonly imageUrl?: string | null;
  readonly imageUrlDark?: string | null;
  /** Public sales-point DTO square logo. */
  readonly tenantLogoUrl?: string | null;
  readonly tenantLogoUrlDark?: string | null;
  /** Public tenant-directory square logo (`ConsumerPublicTenantRow.logoUrl`). */
  readonly logoUrl?: string | null;
  readonly logoUrlDark?: string | null;
};

function resolveLiveTenantLogoUrl(
  source: PendingFavoriteLiveBrandingSource,
): string | null | undefined {
  if (source.tenantLogoUrl !== undefined) {
    return source.tenantLogoUrl;
  }
  if (source.logoUrl !== undefined) {
    return source.logoUrl;
  }
  return undefined;
}

function resolveLiveTenantLogoUrlDark(
  source: PendingFavoriteLiveBrandingSource,
): string | null | undefined {
  if (source.tenantLogoUrlDark !== undefined) {
    return source.tenantLogoUrlDark;
  }
  if (source.logoUrlDark !== undefined) {
    return source.logoUrlDark;
  }
  return undefined;
}

function brandingEquals(
  item: StoredPendingFavoriteSpContact,
  tenantLogoUrl: string | null | undefined,
  tenantLogoUrlDark: string | null | undefined,
  imageUrl: string | null | undefined,
  imageUrlDark: string | null | undefined,
  rim: LogoChipRimSettings,
  background: LogoChipBackgroundSettings,
  patchLogo: boolean,
  patchLogoDark: boolean,
  patchImage: boolean,
  patchImageDark: boolean,
): boolean {
  const currentRim = normalizeLogoChipRimSettings(item);
  const currentBackground = normalizeLogoChipBackgroundSettings(item);
  const logoSame = !patchLogo || item.tenantLogoUrl === tenantLogoUrl;
  const logoDarkSame = !patchLogoDark || item.tenantLogoUrlDark === tenantLogoUrlDark;
  const imageSame = !patchImage || item.imageUrl === imageUrl;
  const imageDarkSame = !patchImageDark || item.imageUrlDark === imageUrlDark;
  return (
    logoSame &&
    logoDarkSame &&
    imageSame &&
    imageDarkSame &&
    currentRim.showLogoChipRimLight === rim.showLogoChipRimLight &&
    currentRim.showLogoChipRimDark === rim.showLogoChipRimDark &&
    currentRim.logoChipRimColorLight === rim.logoChipRimColorLight &&
    currentRim.logoChipRimColorDark === rim.logoChipRimColorDark &&
    currentBackground.showLogoChipBackgroundLight === background.showLogoChipBackgroundLight &&
    currentBackground.showLogoChipBackgroundDark === background.showLogoChipBackgroundDark &&
    currentBackground.logoChipBackgroundColorLight === background.logoChipBackgroundColorLight &&
    currentBackground.logoChipBackgroundColorDark === background.logoChipBackgroundColorDark
  );
}

function matchLiveBrandingSource(
  item: StoredPendingFavoriteSpContact,
  bySalesPointId: Map<number, PendingFavoriteLiveBrandingSource>,
  bySlugKey: Map<string, PendingFavoriteLiveBrandingSource>,
  byTenantCode: Map<string, PendingFavoriteLiveBrandingSource>,
): PendingFavoriteLiveBrandingSource | undefined {
  if (typeof item.salesPointId === 'number' && item.salesPointId > 0) {
    const byId = bySalesPointId.get(item.salesPointId);
    if (byId !== undefined) {
      return byId;
    }
  }
  const slug =
    (typeof item.salesPointSlug === 'string' && item.salesPointSlug.length > 0
      ? item.salesPointSlug
      : item.code) ?? '';
  if (slug.length > 0) {
    const bySlug = bySlugKey.get(
      `${item.tenantCode.trim().toLowerCase()}|${slug.trim().toLowerCase()}`,
    );
    if (bySlug !== undefined) {
      return bySlug;
    }
  }
  return byTenantCode.get(item.tenantCode.trim().toLowerCase());
}

/**
 * G8 — patch pending favourite logo/rim/background/store photo from live public SP/tenant DTOs.
 * Does not bump `storedAt` / TTL. Returns how many entries changed.
 * Prefer calling after `tenant_branding_updated` invalidates public directory
 * caches (allowlist drain / sales-points / tenants refetch).
 */
export function mergePendingFavoriteBrandingFromLive(
  sources: readonly PendingFavoriteLiveBrandingSource[],
  nowMs: number = Date.now(),
): number {
  if (sources.length === 0) {
    return 0;
  }

  const bySalesPointId = new Map<number, PendingFavoriteLiveBrandingSource>();
  const bySlugKey = new Map<string, PendingFavoriteLiveBrandingSource>();
  const byTenantCode = new Map<string, PendingFavoriteLiveBrandingSource>();

  for (const source of sources) {
    if (typeof source.salesPointId === 'number' && source.salesPointId > 0) {
      bySalesPointId.set(source.salesPointId, source);
    }
    const tenant = (source.tenantCode ?? '').trim().toLowerCase();
    const slug = (
      (typeof source.salesPointSlug === 'string' && source.salesPointSlug.length > 0
        ? source.salesPointSlug
        : source.code) ?? ''
    )
      .trim()
      .toLowerCase();
    if (tenant.length > 0 && slug.length > 0) {
      bySlugKey.set(`${tenant}|${slug}`, source);
    }
    // Tenant-directory rows (logoUrl, no SP id) — fill tenant fallback map only.
    if (
      tenant.length > 0 &&
      source.logoUrl !== undefined &&
      source.tenantLogoUrl === undefined &&
      !(typeof source.salesPointId === 'number' && source.salesPointId > 0)
    ) {
      byTenantCode.set(tenant, source);
    }
  }

  if (bySalesPointId.size === 0 && bySlugKey.size === 0 && byTenantCode.size === 0) {
    return 0;
  }

  const current = pruneAndDedupe(readRaw(), nowMs);
  let changed = 0;
  const next = current.map((item) => {
    const source = matchLiveBrandingSource(item, bySalesPointId, bySlugKey, byTenantCode);
    if (source === undefined) {
      return item;
    }
    const rim = normalizeLogoChipRimSettings(source);
    const background = normalizeLogoChipBackgroundSettings(source);
    const liveLogo = resolveLiveTenantLogoUrl(source);
    const patchLogo = liveLogo !== undefined;
    const liveLogoDark = resolveLiveTenantLogoUrlDark(source);
    const patchLogoDark = liveLogoDark !== undefined;
    const liveImage = source.imageUrl !== undefined ? source.imageUrl : undefined;
    const patchImage = liveImage !== undefined;
    const liveImageDark = source.imageUrlDark !== undefined ? source.imageUrlDark : undefined;
    const patchImageDark = liveImageDark !== undefined;
    if (
      brandingEquals(
        item,
        liveLogo,
        liveLogoDark,
        liveImage,
        liveImageDark,
        rim,
        background,
        patchLogo,
        patchLogoDark,
        patchImage,
        patchImageDark,
      )
    ) {
      return item;
    }
    changed += 1;
    return {
      ...item,
      ...(patchLogo ? { tenantLogoUrl: liveLogo } : {}),
      ...(patchLogoDark ? { tenantLogoUrlDark: liveLogoDark } : {}),
      ...(patchImage ? { imageUrl: liveImage } : {}),
      ...(patchImageDark ? { imageUrlDark: liveImageDark } : {}),
      ...rim,
      ...background,
    };
  });

  if (changed > 0) {
    writeRaw(next);
  }
  return changed;
}
